// Helpers for the Peter Data Pro chat UI: token-count formatting, safe markdown rendering and the
// typewriter split. (The old client-side scoping helpers went with the manual condition picker.)

// Compact human-readable token count: 999 -> "999", 18700 -> "18.7K", 2_500_000 -> "2.5M".
export function formatTokenCount(n) {
  var v = Number(n) || 0;
  if (v < 1000) return String(Math.round(v));
  if (v < 1000000) return (v / 1000).toFixed(1) + 'K';
  return (v / 1000000).toFixed(1) + 'M';
}

// Render Peter's reply to safe HTML. Plain prose by default (with **bold**, `- ` bullets and
// [text](https://…) links, which open in a new tab; only http(s) becomes a link), plus GitHub-style
// markdown tables → real <table> (so columns auto-align in the browser).
// HTML is escaped first, and only our own tags are emitted, so it is safe for v-html.
export function renderMarkdown(text) {
  if (!text) return '';

  const esc = (s) => s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const link = (url, label) => '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + label + '</a>';
  const inline = (s) => s
    .replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g, (m, label, url) => link(url, label))
    // a bare URL the model did not wrap (not one already inside an href/anchor); trailing punctuation stays outside
    .replace(/(^|[^"'>])(https?:\/\/[^\s<]+)/g, (m, pre, url) => {
      const trail = (url.match(/[.,;:!?)]+$/) || [''])[0];
      const clean = trail ? url.slice(0, -trail.length) : url;
      return pre + link(clean, clean) + trail;
    });
  const isRow = (l) => l.indexOf('|') !== -1;
  // A separator row: only |, -, :, and spaces, with at least one dash.
  const isSep = (l) => /-/.test(l) && /^[\s|:-]+$/.test(l) && l.indexOf('|') !== -1;

  const splitCells = (l) => {
    let t = l.trim();
    if (t.charAt(0) === '|') t = t.slice(1);
    if (t.charAt(t.length - 1) === '|') t = t.slice(0, -1);
    return t.split('|').map((c) => c.trim());
  };

  const lines = text.split('\n');
  const blocks = [];
  let i = 0;
  while (i < lines.length) {
    // A table is a row line immediately followed by a separator row.
    if (i + 1 < lines.length && isRow(lines[i]) && !isSep(lines[i]) && isSep(lines[i + 1])) {
      const header = splitCells(lines[i]);
      i += 2; // consume header + separator
      const rows = [];
      while (i < lines.length && isRow(lines[i]) && !isSep(lines[i])) {
        rows.push(splitCells(lines[i]));
        i += 1;
      }
      let html = '<div class="aps-md-tablewrap"><table class="aps-md-table"><thead><tr>';
      header.forEach((h) => { html += '<th>' + inline(esc(h)) + '</th>'; });
      html += '</tr></thead><tbody>';
      rows.forEach((r) => {
        html += '<tr>';
        for (let c = 0; c < header.length; c += 1) {
          html += '<td>' + inline(esc(r[c] != null ? r[c] : '')) + '</td>';
        }
        html += '</tr>';
      });
      html += '</tbody></table></div>';
      blocks.push({ table: true, html });
    } else {
      let line = esc(lines[i]).replace(/^[-*]\s+/, '• ');
      blocks.push({ table: false, html: inline(line) });
      i += 1;
    }
  }

  // Join consecutive prose lines with <br>; tables stand alone (no <br> hugging them).
  let out = '';
  let buf = [];
  const flush = () => { if (buf.length) { out += buf.join('<br>'); buf = []; } };
  blocks.forEach((b) => {
    if (b.table) { flush(); out += b.html; } else { buf.push(b.html); }
  });
  flush();
  return out;
}

// Split a finished reply into the ordered pieces a client-side "typewriter" reveals one at a time:
// prose is broken into words (so it types out), but a GitHub-style markdown table is kept as ONE
// piece so it appears as a whole block instead of rendering half-built. Concatenating all pieces in
// order reproduces the original text exactly.
export function splitForReveal(text) {
  if (!text) return [];
  const lines = text.split('\n');
  const isRow = (l) => l.indexOf('|') !== -1;
  const isSep = (l) => /-/.test(l) && /^[\s|:-]+$/.test(l) && l.indexOf('|') !== -1;
  const pieces = [];
  let first = true;
  let i = 0;
  while (i < lines.length) {
    const nl = first ? '' : '\n';   // the newline that precedes this line/block
    if (i + 1 < lines.length && isRow(lines[i]) && !isSep(lines[i]) && isSep(lines[i + 1])) {
      // a table (header + separator + rows) — reveal the whole block at once
      let j = i + 2;
      while (j < lines.length && isRow(lines[j]) && !isSep(lines[j])) j += 1;
      pieces.push(nl + lines.slice(i, j).join('\n'));
      first = false;
      i = j;
    } else {
      const words = lines[i].length ? (lines[i].match(/\S+\s*/g) || [lines[i]]) : [];
      if (words.length === 0) {
        if (nl) pieces.push(nl);   // blank line
      } else {
        words.forEach((w, idx) => pieces.push(idx === 0 ? nl + w : w));
      }
      first = false;
      i += 1;
    }
  }
  return pieces;
}
